import {cloneElement, ReactElement, ReactNode} from 'react';
import {IconButton, IconButtonProps} from '@ui/buttons/icon-button';
import {MoreHorizIcon} from '@ui/icons/material/MoreHoriz';
import {PlayableModel} from '@app/web-player/playable-item/playable-model';
import {PlaybackToggleButton} from '@app/web-player/playable-item/playback-toggle-button';
import {DialogTrigger} from '@ui/overlays/dialog/dialog-trigger';
import {StackTrigger} from '@ui/overlays/stack-navigator/stack-trigger';
import {queueGroupId} from '@app/web-player/queue-group-id';
import clsx from 'clsx';
import {useNavigate} from '@common/ui/navigation/use-navigate';
import {Track, TRACK_MODEL} from '@app/web-player/tracks/track';
import { isIOSOrAndroid } from '@common/core/platform-detection';
import { ChipList } from '@ui/forms/input-field/chip-field/chip-list';
import { Chip } from '@ui/forms/input-field/chip-field/chip';
import { Trans } from '@ui/i18n/trans';
import {FormattedDate} from '@ui/i18n/formatted-date';
import { useUserRolesAccess } from '@common/auth/user-roles-access';
import { canPlayAlbum } from '@app/web-player/albums/utils/album-filter';
import { canPlayTrack } from '@app/web-player/tracks/utils/track-filter';
import { ARTIST_MODEL } from '../artists/artist';
import { ALBUM_MODEL } from '../albums/album';
import { useAuth } from '@common/auth/use-auth';
import { LIVE_EVENT_MODEL } from '@app/admin/live-events/interfaces/live-event';

interface PlayableGridProps {
  item?: any;
  image: ReactElement;
  title: ReactNode;
  subtitle?: ReactNode;
  model: PlayableModel;
  newQueue?: Track[];
  link: string;
  likeButton?: ReactElement<IconButtonProps>;
  contextDialog: ReactElement;
  titleDialog?: string;
  radius?: string;
  contentFormat?: string;
  canPlay?: boolean;
}
export function PlayableGridItem({
  item,
  image,
  title,
  subtitle,
  model,
  newQueue,
  link,
  likeButton,
  contextDialog,
  titleDialog,
  radius = 'rounded-panel',
  contentFormat,
  canPlay = true
}: PlayableGridProps) {
  const navigate = useNavigate();
  const {user} = useAuth();
  const {userAccess} = useUserRolesAccess();
  const itemType = Object.keys(item.props)[0];
  const itemValue = item.props[itemType];
  const isArtist = model.model_type === ARTIST_MODEL;
  const isLiveEvent = model.model_type === LIVE_EVENT_MODEL;
  const isMedia = model.model_type === ALBUM_MODEL || model.model_type === TRACK_MODEL;
  const hasBadge = !isArtist && (
    itemValue.isAvailable === false || 
    itemValue.isPublished === false || 
    itemValue.isEncoded === false || 
    itemValue.isReleased === false || 
    itemValue.isExclusivity === true 
  );
  
  const trackPlayFilterOptions = { permissions: userAccess.medias.play, userId: user?.id };
  const canPlayItem = canPlay ? 
    (
      itemValue.model_type === ALBUM_MODEL
      ? canPlayAlbum(itemValue, trackPlayFilterOptions)
      : (
        itemValue.model_type === TRACK_MODEL
          ? canPlayTrack(itemValue, trackPlayFilterOptions)
          : true
      )
    ) : false;

  return (
    <div className="snap-start snap-normal">
      <DialogTrigger
        type="popover"
        placement="bottom-start"
        mobileType="tray"
        triggerOnContextMenu
      >
        <div className="group relative isolate w-full">
          
          {hasBadge && (
            <div className="absolute top-8 right-8 z-10">
              <ChipList size="xs" color="positive" radius='rounded-full' className='justify-end'>
                {!itemValue.isAvailable ? (
                  <Chip color='danger'>
                    <span className={clsx('capitalize')}><Trans message="Unavailable" /></span>
                  </Chip>
                ) : <></>}
                {isMedia && !itemValue.isEncoded ? (
                  <Chip color="chip">
                    <span className={clsx('capitalize')}><Trans message="Unencoded" /></span>
                  </Chip>
                ) : <></>}
                {isMedia && !itemValue.isReleased ? (
                  <Chip>
                    <span className={clsx('capitalize')}><Trans message="Out Soon" /></span>
                  </Chip>
                ) : <></>}
                {isMedia && itemValue.isExclusivity ? (
                  <Chip color="primary">
                    <span className={clsx('capitalize')}><Trans message="Exclusive" /></span>
                  </Chip>
                ) : <></>}
                
                {isLiveEvent && !itemValue.isPublished ? (
                  <Chip color="chip">
                    <span className={clsx('capitalize')}><Trans message="Unpublished" /></span>
                  </Chip>
                ) : <></>}
              </ChipList>
            </div>
          )}

          {!isIOSOrAndroid() || !item ? (
            <div
              className={clsx(
                "this w-full",
                {
                  'aspect-[16/9]': contentFormat === 'landscape',
                  'aspect-[9/16]': contentFormat === 'portrait',
                  'aspect-[4/5]': contentFormat === 'poster',
                  'aspect-[1/1]': !contentFormat || contentFormat && !['landscape', 'portrait', 'poster'].includes(contentFormat)
                }
              )}
              onClick={() => navigate(link)}
            >
              {cloneElement(image, {
                size: (
                  contentFormat === 'landscape' ? 'w-full h-full aspect-[16/9]' : (
                    contentFormat === 'portrait' ? 'w-full h-full aspect-[9/16]'  : (
                      contentFormat === 'poster' ? 'w-full h-full aspect-[4/5]' : 'w-full h-full aspect-[1/1]'
                    )
                  )
                ),
                className: `${radius} shadow-md z-10`,
              })}
            </div>
          ) : (
            <StackTrigger moveFocusToDialog={false} title={titleDialog}>
              <div
                className={clsx(
                  "this w-full",
                  {
                    'aspect-[16/9]': contentFormat === 'landscape',
                    'aspect-[9/16]': contentFormat === 'portrait',
                    'aspect-[4/5]': contentFormat === 'poster',
                    'aspect-[1/1]': !contentFormat || contentFormat && !['landscape', 'portrait', 'poster'].includes(contentFormat)
                  }
                )}
              >
                {cloneElement(image, {
                  size: (
                    contentFormat === 'landscape' ? 'w-full h-full aspect-[16/9]' : (
                      contentFormat === 'portrait' ? 'w-full h-full aspect-[9/16]' : (
                        contentFormat === 'poster' ? 'w-full h-full aspect-[4/5]' : 'w-full h-full aspect-[1/1]'
                      )
                    )
                  ),
                  className: `${radius} shadow-md z-10`,
                })}
              </div>
              {item}
            </StackTrigger>
          )}
          
          {canPlayItem && (
            <>
              <div
                key="bg-overlay"
                className={`absolute left-0 top-0 h-full w-full bg-gradient-to-b from-transparent to-black/75 ${radius} pointer-events-none z-20 opacity-0 transition-opacity duration-300 group-hover:opacity-100`}
              />
              <div
                className={clsx(
                  'absolute bottom-0 left-0 z-30 flex w-full items-center gap-14 p-12',
                  radius === 'rounded-full' &&
                    'pointer-events-none right-0 top-0 justify-center',
                )}
              >
                <PlaybackToggleButton
                  size={radius === 'rounded-full' ? 'lg' : 'md'}
                  radius="rounded-full"
                  className={clsx(
                    'pointer-events-auto shadow-md',
                    radius === 'rounded-full' && 'invisible group-hover:visible',
                  )}
                  variant="flat"
                  color="white"
                  buttonType="icon"
                  track={model.model_type === TRACK_MODEL ? model : undefined}
                  tracks={newQueue}
                  queueId={queueGroupId(model)}
                />

                {radius !== 'rounded-full' && (
                  <DialogTrigger type="popover" mobileType="tray">
                    <IconButton
                      className="invisible md:group-hover:visible"
                      color="white"
                    >
                      <MoreHorizIcon />
                    </IconButton>
                    {contextDialog}
                  </DialogTrigger>
                )}
                {radius !== 'rounded-full' &&
                  likeButton &&
                  // 3 buttons won't fit if item is fully rounded
                  cloneElement(likeButton, {
                    className: 'invisible md:group-hover:visible ml-auto',
                    size: 'md',
                    color: 'white',
                  })}
              </div>
            </>
          )}
        </div>
        {contextDialog}
      </DialogTrigger>
      <div
        className={clsx(
          radius === 'rounded-full' && 'text-center',
          'mt-12 text-sm',
        )}
      >
        <div className="truncate">{title}</div>
        <div className="mt-4 truncate text-muted">
          {subtitle}
        </div>
        {!isArtist && !itemValue.isReleased ? (
          <div className="mt-4 truncate text-muted text-xs">
            <FormattedDate date={itemValue.releaseDate} />
          </div>
        ) : <></>}
      </div>
    </div>
  );
}

interface DefaultGridElementProps {
  image: ReactElement;
  title: ReactElement;
  subtitle?: ReactElement;
  link?: string;
  radius?: string;
  contentFormat?: 'portrait' | 'landscape' | 'square' | 'poster' | string | undefined;
  titleDialog?: string;
}

export function DefaultGridElement({
  image,
  title,
  subtitle,
  radius = 'rounded-panel',
  contentFormat = 'square'
}: DefaultGridElementProps) {
  const navigate = useNavigate();

  return (
    <div className="snap-start snap-normal">
      <div className="group relative isolate w-full">
        <div
          className={clsx(
            "this w-full cursor-pointer",
            {
              'aspect-[16/9]': contentFormat === 'landscape',
              'aspect-[9/16]': contentFormat === 'portrait',
              'aspect-[4/5]': contentFormat === 'poster',
              'aspect-[1/1]': contentFormat === 'square',
            }
          )}
        >
          {cloneElement(image, {
            size: (
              contentFormat === 'landscape' ? 'w-full h-full aspect-[16/9]' :
              contentFormat === 'portrait' ? 'w-full h-full aspect-[9/16]' :
              contentFormat === 'poster' ? 'w-full h-full aspect-[4/5]' :
              'w-full h-full aspect-[1/1]'
            ),
            className: `${radius} shadow-md z-10`,
          })}
        </div>
      </div>

      <div
        className={clsx(
          radius === 'rounded-full' && 'text-center',
          'mt-12 text-sm'
        )}
      >
        <div className="truncate">{title}</div>
        {subtitle && (
          <div className="mt-4 truncate text-muted">
            {subtitle}
          </div>
        )}
      </div>
    </div>
  );
}
